// ============================================================
// FloraIQ — MOCK DATA: Relatórios
// Tipos: Técnico, Executivo, Fotográfico, Cartográfico/GIS,
//   Inventário arbóreo, FCA individual, Árvores de risco,
//   Manejo recomendado, Espécies, Bairro/logradouro,
//   Área verde, UC, Carbono, Compensação ambiental,
//   Documental, RRR/Governança, Auditoria, Transparência pública
// ============================================================

export interface ReportItem {
  id: string;
  title: string;
  description: string;
  type: string;
  projectName: string;
  municipality: string;
  uf: string;
  period: string;
  responsible: string;
  format: string;
  status: string;
  generatedAt: string;
  isPublic: boolean;
  tags: string[];
}

export interface ReportTemplate {
  id: string;
  name: string;
  purpose: string;
  audience: string;
  sections: string[];
  formats: string[];
  status: string;
  icon: string;
}

export interface CtaReport {
  id: string;
  title: string;
  description: string;
  indicators: string[];
  lastGenerated: string;
  type: string;
}

export interface GisExport {
  id: string;
  layer: string;
  project: string;
  municipality: string;
  attributeFilter: string;
  srid: string;
  format: string;
  featureCount: number;
  lastExported: string;
  status: string;
}

export const reportTypes = [
  "Técnico",
  "Executivo",
  "Fotográfico",
  "Cartográfico/GIS",
  "Inventário arbóreo",
  "FCA individual",
  "Árvores de risco",
  "Manejo recomendado",
  "Espécies",
  "Bairro/logradouro",
  "Área verde",
  "Unidade de Conservação",
  "Carbono",
  "Compensação ambiental",
  "Documental",
  "RRR / Governança",
  "Auditoria",
  "Transparência pública",
];

export const reportStatuses = [
  "Rascunho",
  "Em processamento",
  "Gerado",
  "Em revisão",
  "Aprovado",
  "Publicado",
  "Com erro",
  "Arquivado",
];

export const reportFormats = ["PDF", "XLSX", "CSV", "DOCX", "GeoJSON", "Shapefile", "KML/KMZ"];

export const reports: ReportItem[] = [
  {
    id: "rep-001",
    title: "Inventário Arbóreo — Parque das Acácias",
    description: "Relatório técnico completo do inventário arbóreo do Parque das Acácias com 847 indivíduos cadastrados, análise fitossanitária e recomendações de manejo.",
    type: "Inventário arbóreo",
    projectName: "Inventário Parque das Acácias",
    municipality: "São Paulo",
    uf: "SP",
    period: "Jan/2026 – Mai/2026",
    responsible: "Eng. Flora Mendes",
    format: "PDF",
    status: "Aprovado",
    generatedAt: "2026-05-28",
    isPublic: true,
    tags: ["inventário", "parque", "árvores"],
  },
  {
    id: "rep-002",
    title: "Relatório Executivo — Prefeitura de Campinas",
    description: "Sumário executivo para a Secretaria Municipal de Meio Ambiente com indicadores gerais do CTA urbano, estado fitossanitário e prioridades de manejo.",
    type: "Executivo",
    projectName: "CTA Urbano Campinas",
    municipality: "Campinas",
    uf: "SP",
    period: "2026",
    responsible: "Dra. Ana Oliveira",
    format: "PDF",
    status: "Publicado",
    generatedAt: "2026-06-10",
    isPublic: true,
    tags: ["executivo", "prefeitura", "gestão"],
  },
  {
    id: "rep-003",
    title: "Relatório Fotográfico — Vistoria Quadra 14",
    description: "Registro fotográfico completo da vistoria realizada na Quadra 14, com 42 fotos georreferenciadas e anotações técnicas por indivíduo arbóreo.",
    type: "Fotográfico",
    projectName: "CTA Urbano Campinas",
    municipality: "Campinas",
    uf: "SP",
    period: "Mar/2026",
    responsible: "Téc. Carlos Santos",
    format: "PDF",
    status: "Gerado",
    generatedAt: "2026-03-22",
    isPublic: false,
    tags: ["fotos", "vistoria", "campo"],
  },
  {
    id: "rep-004",
    title: "Mapa de Árvores de Risco — Zona Central",
    description: "Exportação cartográfica com pontos de árvores classificadas como risco alto e crítico na zona central, com buffers de interferência.",
    type: "Cartográfico/GIS",
    projectName: "Inventário Zona Central",
    municipality: "São Paulo",
    uf: "SP",
    period: "2026",
    responsible: "GIS Ana Beatriz Costa",
    format: "GeoJSON",
    status: "Gerado",
    generatedAt: "2026-06-15",
    isPublic: false,
    tags: ["mapa", "risco", "geojson"],
  },
  {
    id: "rep-005",
    title: "Relatório de Manejo Recomendado — Loteamento Green Park",
    description: "Relatório com recomendações de poda, tratamento fitossanitário e supressão para 134 árvores do loteamento Green Park.",
    type: "Manejo recomendado",
    projectName: "Loteamento Green Park",
    municipality: "Jundiaí",
    uf: "SP",
    period: "Abr/2026",
    responsible: "Eng. Rafael Torres",
    format: "PDF",
    status: "Em revisão",
    generatedAt: "2026-04-18",
    isPublic: false,
    tags: ["manejo", "poda", "loteamento"],
  },
  {
    id: "rep-006",
    title: "FCA Individual — CTA-2024-08473",
    description: "Ficha Cadastral Arbórea completa para o indivíduo CTA-2024-08473 (Tipuana tipu), com dados dendrométricos, fotos e histórico de vistorias.",
    type: "FCA individual",
    projectName: "CTA Urbano Campinas",
    municipality: "Campinas",
    uf: "SP",
    period: "2026",
    responsible: "Téc. Carlos Santos",
    format: "PDF",
    status: "Gerado",
    generatedAt: "2026-05-03",
    isPublic: false,
    tags: ["fca", "tipuana", "individual"],
  },
  {
    id: "rep-007",
    title: "Relatório de Espécies — Diversidade Arbórea Urbana",
    description: "Levantamento de espécies encontradas no inventário municipal, com distribuição por família, frequência e análise de espécies exóticas vs nativas.",
    type: "Espécies",
    projectName: "CTA Urbano Campinas",
    municipality: "Campinas",
    uf: "SP",
    period: "2026",
    responsible: "Biól. Dra. Marina Costa",
    format: "PDF",
    status: "Aprovado",
    generatedAt: "2026-06-05",
    isPublic: true,
    tags: ["espécies", "diversidade", "botânica"],
  },
  {
    id: "rep-008",
    title: "Relatório de Carbono Urbano — Estimativas Preliminares 2026",
    description: "Estimativas preliminares de biomassa, carbono estocado e CO2e com base nos dados dendrométricos de 10.932 árvores do CTA.",
    type: "Carbono",
    projectName: "Carbono Urbano Municipal",
    municipality: "Campinas",
    uf: "SP",
    period: "2026",
    responsible: "Eng. Flora Mendes",
    format: "PDF",
    status: "Gerado",
    generatedAt: "2026-06-18",
    isPublic: true,
    tags: ["carbono", "co2e", "biomassa"],
  },
  {
    id: "rep-009",
    title: "Relatório de UC — Reserva Ecológica Mata Verde",
    description: "Relatório de gestão da Unidade de Conservação, com inventário arbóreo, plano de manejo, espécies ameaçadas e monitoramento.",
    type: "Unidade de Conservação",
    projectName: "Gestão UC Mata Verde",
    municipality: "Campinas",
    uf: "SP",
    period: "2025-2026",
    responsible: "Gestor Lucas Almeida",
    format: "PDF",
    status: "Aprovado",
    generatedAt: "2026-05-15",
    isPublic: false,
    tags: ["uc", "plano de manejo", "conservação"],
  },
  {
    id: "rep-010",
    title: "Relatório de Compensação Ambiental — Empreendimento Rio Bonito",
    description: "Relatório de monitoramento da compensação ambiental do empreendimento Rio Bonito, com 1.200 mudas plantadas e taxa de sobrevivência de 87%.",
    type: "Compensação ambiental",
    projectName: "Compensação Rio Bonito",
    municipality: "Jundiaí",
    uf: "SP",
    period: "2025-2026",
    responsible: "Eng. Rafael Torres",
    format: "PDF",
    status: "Em revisão",
    generatedAt: "2026-06-12",
    isPublic: false,
    tags: ["compensação", "mudas", "replantio"],
  },
  {
    id: "rep-011",
    title: "Relatório Documental — Índice do Projeto CTA Vinhedo",
    description: "Índice documental completo do projeto CTA Vinhedo com 248 documentos vinculados, incluindo laudos, autorizações e bases GIS.",
    type: "Documental",
    projectName: "CTA Vinhedo",
    municipality: "Vinhedo",
    uf: "SP",
    period: "2025-2026",
    responsible: "Adm. Patrícia Nunes",
    format: "PDF",
    status: "Gerado",
    generatedAt: "2026-06-08",
    isPublic: false,
    tags: ["documental", "índice", "gestão"],
  },
  {
    id: "rep-012",
    title: "Relatório RRR — Governança do Projeto CTA Zona Sul",
    description: "Relatório de responsabilidades, restrições e autorizações vinculadas ao projeto CTA Zona Sul, conforme modelo LADM-FloraIQ.",
    type: "RRR / Governança",
    projectName: "CTA Zona Sul",
    municipality: "São Paulo",
    uf: "SP",
    period: "2026",
    responsible: "Jur. Dr. Ricardo Moura",
    format: "PDF",
    status: "Em processamento",
    generatedAt: "2026-06-20",
    isPublic: false,
    tags: ["rrr", "governança", "ladm"],
  },
  {
    id: "rep-013",
    title: "Relatório de Auditoria — Conformidade Inventário Guarulhos",
    description: "Auditoria técnica de qualidade dos dados do inventário de Guarulhos, com verificação de completude cadastral e consistência geoespacial.",
    type: "Auditoria",
    projectName: "Inventário Guarulhos",
    municipality: "Guarulhos",
    uf: "SP",
    period: "2026",
    responsible: "Aud. Fernando Lopes",
    format: "PDF",
    status: "Em revisão",
    generatedAt: "2026-06-14",
    isPublic: false,
    tags: ["auditoria", "qualidade", "conformidade"],
  },
  {
    id: "rep-014",
    title: "Relatório de Transparência Pública — CTA Municipal",
    description: "Relatório de transparência pública com indicadores do patrimônio arbóreo municipal para divulgação no portal da prefeitura.",
    type: "Transparência pública",
    projectName: "CTA Urbano Campinas",
    municipality: "Campinas",
    uf: "SP",
    period: "2026",
    responsible: "Dra. Ana Oliveira",
    format: "PDF",
    status: "Publicado",
    generatedAt: "2026-06-01",
    isPublic: true,
    tags: ["transparência", "público", "prefeitura"],
  },
  {
    id: "rep-015",
    title: "Relatório de Área Verde — Parque Central",
    description: "Relatório detalhado da área verde Parque Central, com 312 árvores cadastradas, estado de conservação e propostas de melhoria.",
    type: "Área verde",
    projectName: "Áreas Verdes Municipais",
    municipality: "Jundiaí",
    uf: "SP",
    period: "2026",
    responsible: "Eng. Flora Mendes",
    format: "PDF",
    status: "Gerado",
    generatedAt: "2026-05-20",
    isPublic: true,
    tags: ["área verde", "parque", "conservação"],
  },
  {
    id: "rep-016",
    title: "Relatório de Árvores de Risco — Vias Arteriais",
    description: "Levantamento de árvores com risco alto e crítico nas vias arteriais, com recomendações de manejo emergencial e prazos.",
    type: "Árvores de risco",
    projectName: "Inventário Zona Central",
    municipality: "São Paulo",
    uf: "SP",
    period: "Mai/2026",
    responsible: "Eng. Rafael Torres",
    format: "PDF",
    status: "Aprovado",
    generatedAt: "2026-05-25",
    isPublic: false,
    tags: ["risco", "emergencial", "vias"],
  },
  {
    id: "rep-017",
    title: "Relatório por Bairro — Jardim Europa",
    description: "Relatório completo do inventário arbóreo do bairro Jardim Europa com 624 árvores, análise por quadra e recomendações de manejo.",
    type: "Bairro/logradouro",
    projectName: "CTA Urbano Campinas",
    municipality: "Campinas",
    uf: "SP",
    period: "2026",
    responsible: "Téc. Carlos Santos",
    format: "PDF",
    status: "Gerado",
    generatedAt: "2026-06-03",
    isPublic: true,
    tags: ["bairro", "regional", "campinas"],
  },
  {
    id: "rep-018",
    title: "Relatório de Progresso — CTA Vinhedo Fase 2",
    description: "Relatório de progresso da Fase 2 do inventário de Vinhedo, com 68% de conclusão e indicadores de produtividade da equipe de campo.",
    type: "Técnico",
    projectName: "CTA Vinhedo",
    municipality: "Vinhedo",
    uf: "SP",
    period: "Jun/2026",
    responsible: "Coord. Márcio Silva",
    format: "PDF",
    status: "Gerado",
    generatedAt: "2026-06-19",
    isPublic: false,
    tags: ["progresso", "fase", "campo"],
  },
  {
    id: "rep-019",
    title: "Relatório ES G Ambiental — Corporativo 2026",
    description: "Relatório de indicadores ambientais para ESG corporativo, com dados de carbono, compensações e sustentabilidade urbana.",
    type: "Carbono",
    projectName: "Carbono Urbano Municipal",
    municipality: "Campinas",
    uf: "SP",
    period: "2026",
    responsible: "Dra. Ana Oliveira",
    format: "PDF",
    status: "Em processamento",
    generatedAt: "",
    isPublic: false,
    tags: ["esg", "corporativo", "sustentabilidade"],
  },
  {
    id: "rep-020",
    title: "Exportação GIS — Árvores CTA em GeoJSON",
    description: "Exportação geoespacial de todos os pontos de árvores do CTA no formato GeoJSON com atributos completos.",
    type: "Cartográfico/GIS",
    projectName: "CTA Urbano Campinas",
    municipality: "Campinas",
    uf: "SP",
    period: "2026",
    responsible: "GIS Ana Beatriz Costa",
    format: "GeoJSON",
    status: "Gerado",
    generatedAt: "2026-06-17",
    isPublic: false,
    tags: ["gis", "geojson", "exportação"],
  },
  {
    id: "rep-021",
    title: "Relatório de Qualidade — Inventário Guarulhos",
    description: "Painel de qualidade dos dados do inventário de Guarulhos com métricas de completude cadastral, acurácia geoespacial e consistência botânica.",
    type: "Auditoria",
    projectName: "Inventário Guarulhos",
    municipality: "Guarulhos",
    uf: "SP",
    period: "2026",
    responsible: "Aud. Fernando Lopes",
    format: "PDF",
    status: "Rascunho",
    generatedAt: "",
    isPublic: false,
    tags: ["qualidade", "métricas", "cadastro"],
  },
  {
    id: "rep-022",
    title: "Relatório de Vistorias — 1º Semestre 2026",
    description: "Consolidado de vistorias realizadas no primeiro semestre de 2026, com 1.847 registros, distribuição por tipo e equipe.",
    type: "Técnico",
    projectName: "CTA Urbano Campinas",
    municipality: "Campinas",
    uf: "SP",
    period: "Jan/2026 – Jun/2026",
    responsible: "Coord. Márcio Silva",
    format: "XLSX",
    status: "Gerado",
    generatedAt: "2026-06-20",
    isPublic: false,
    tags: ["vistorias", "semestral", "consolidado"],
  },
  {
    id: "rep-023",
    title: "Relatório de Supressões Autorizadas — 2026",
    description: "Compilado de autorizações de supressão emitidas em 2026, com justificativas técnicas, compensações vinculadas e status de execução.",
    type: "Documental",
    projectName: "Gestão Ambiental Municipal",
    municipality: "São Paulo",
    uf: "SP",
    period: "2026",
    responsible: "Jur. Dr. Ricardo Moura",
    format: "PDF",
    status: "Em revisão",
    generatedAt: "2026-06-16",
    isPublic: false,
    tags: ["supressão", "autorização", "compensação"],
  },
  {
    id: "rep-024",
    title: "Relatório Final — Implantação CTA Jundiaí",
    description: "Relatório final de implantação do CTA em Jundiaí com 8.432 árvores cadastradas, metodologia aplicada e lições aprendidas.",
    type: "Técnico",
    projectName: "CTA Jundiaí",
    municipality: "Jundiaí",
    uf: "SP",
    period: "2025-2026",
    responsible: "Eng. Flora Mendes",
    format: "PDF",
    status: "Aprovado",
    generatedAt: "2026-04-30",
    isPublic: true,
    tags: ["final", "implantação", "cta"],
  },
  {
    id: "rep-025",
    title: "Relatório de Responsabilidades Ativas — LADM",
    description: "Mapeamento completo de responsabilidades ativas conforme modelo LADM-FloraIQ, vinculando partes, fontes e unidades ambientais.",
    type: "RRR / Governança",
    projectName: "Governança Ambiental",
    municipality: "Campinas",
    uf: "SP",
    period: "2026",
    responsible: "Jur. Dr. Ricardo Moura",
    format: "PDF",
    status: "Rascunho",
    generatedAt: "",
    isPublic: false,
    tags: ["rrr", "responsabilidades", "ladm"],
  },
  {
    id: "rep-026",
    title: "Relatório de Pendências — CTA Vinhedo",
    description: "Levantamento de pendências do inventário CTA Vinhedo: 127 árvores sem foto, 43 sem espécie, 18 sem DAP e 9 vistorias atrasadas.",
    type: "Técnico",
    projectName: "CTA Vinhedo",
    municipality: "Vinhedo",
    uf: "SP",
    period: "Jun/2026",
    responsible: "Coord. Márcio Silva",
    format: "PDF",
    status: "Gerado",
    generatedAt: "2026-06-19",
    isPublic: false,
    tags: ["pendências", "qualidade", "cadastro"],
  },
  {
    id: "rep-027",
    title: "Relatório de Autorizações Ambientais — 2025-2026",
    description: "Compilado de autorizações ambientais emitidas no biênio 2025-2026, com status de vigência e execução.",
    type: "Documental",
    projectName: "Gestão Ambiental Municipal",
    municipality: "São Paulo",
    uf: "SP",
    period: "2025-2026",
    responsible: "Adm. Patrícia Nunes",
    format: "PDF",
    status: "Aprovado",
    generatedAt: "2026-06-02",
    isPublic: false,
    tags: ["autorizações", "ambiental", "vigência"],
  },
  {
    id: "rep-028",
    title: "Relatório de CO2e por Área Verde — 2026",
    description: "Distribuição das estimativas de CO2e por área verde municipal, com ranking e contribuição percentual de cada área.",
    type: "Carbono",
    projectName: "Carbono Urbano Municipal",
    municipality: "Campinas",
    uf: "SP",
    period: "2026",
    responsible: "Eng. Flora Mendes",
    format: "XLSX",
    status: "Gerado",
    generatedAt: "2026-06-18",
    isPublic: true,
    tags: ["co2e", "área verde", "ranking"],
  },
  {
    id: "rep-029",
    title: "Relatório de FCA em Lote — 50 Indivíduos",
    description: "Fichas Cadastrais Arbóreas em lote para 50 indivíduos do Parque das Acácias, com dados completos e fotos.",
    type: "FCA individual",
    projectName: "Inventário Parque das Acácias",
    municipality: "São Paulo",
    uf: "SP",
    period: "2026",
    responsible: "Téc. Carlos Santos",
    format: "PDF",
    status: "Em processamento",
    generatedAt: "",
    isPublic: false,
    tags: ["fca", "lote", "parque"],
  },
  {
    id: "rep-030",
    title: "Relatório de Corredores Verdes — Zona Norte",
    description: "Mapeamento e análise dos corredores verdes da Zona Norte com conectividade ecológica e propostas de expansão.",
    type: "Área verde",
    projectName: "Planejamento Ambiental Urbano",
    municipality: "Campinas",
    uf: "SP",
    period: "2026",
    responsible: "Biól. Dra. Marina Costa",
    format: "PDF",
    status: "Em revisão",
    generatedAt: "2026-06-10",
    isPublic: false,
    tags: ["corredor verde", "conectividade", "planejamento"],
  },
  {
    id: "rep-031",
    title: "Relatório de Documentos Vencidos — Municipal",
    description: "Levantamento de documentos e autorizações vencidos ou a vencer em 60 dias no âmbito municipal.",
    type: "Documental",
    projectName: "Gestão Documental Municipal",
    municipality: "Campinas",
    uf: "SP",
    period: "2026",
    responsible: "Adm. Patrícia Nunes",
    format: "PDF",
    status: "Com erro",
    generatedAt: "2026-06-20",
    isPublic: false,
    tags: ["vencidos", "documental", "alerta"],
  },
  {
    id: "rep-032",
    title: "Relatório de Equipe — Produtividade Campo 2026",
    description: "Análise de produtividade da equipe de campo no primeiro semestre de 2026, com médias diárias e comparação entre técnicos.",
    type: "Executivo",
    projectName: "CTA Urbano Campinas",
    municipality: "Campinas",
    uf: "SP",
    period: "Jan/2026 – Jun/2026",
    responsible: "Coord. Márcio Silva",
    format: "XLSX",
    status: "Arquivado",
    generatedAt: "2026-06-15",
    isPublic: false,
    tags: ["equipe", "produtividade", "campo"],
  },
  {
    id: "rep-033",
    title: "Relatório de APPs Urbanas — Margens de Córregos",
    description: "Diagnóstico das Áreas de Preservação Permanente urbanas ao longo dos córregos municipais, com inventário arbóreo e estado de conservação.",
    type: "Área verde",
    projectName: "Diagnóstico APPs Urbanas",
    municipality: "Jundiaí",
    uf: "SP",
    period: "2026",
    responsible: "Biól. Dra. Marina Costa",
    format: "PDF",
    status: "Gerado",
    generatedAt: "2026-06-07",
    isPublic: true,
    tags: ["app", "córrego", "preservação"],
  },
  {
    id: "rep-034",
    title: "Relatório de Obrigações Pendentes — Compensações",
    description: "Mapeamento de obrigações ambientais pendentes vinculadas a compensações, com prazos e responsáveis.",
    type: "RRR / Governança",
    projectName: "Governança Ambiental",
    municipality: "São Paulo",
    uf: "SP",
    period: "2026",
    responsible: "Jur. Dr. Ricardo Moura",
    format: "PDF",
    status: "Em processamento",
    generatedAt: "",
    isPublic: false,
    tags: ["obrigações", "compensações", "pendências"],
  },
  {
    id: "rep-035",
    title: "Relatório Final de Campo — Projeto CTA Zona Sul",
    description: "Relatório final das atividades de campo do projeto CTA Zona Sul, com 3.214 árvores vistoriadas em 45 dias de trabalho.",
    type: "Técnico",
    projectName: "CTA Zona Sul",
    municipality: "São Paulo",
    uf: "SP",
    period: "Mai/2026 – Jun/2026",
    responsible: "Coord. Márcio Silva",
    format: "PDF",
    status: "Gerado",
    generatedAt: "2026-06-19",
    isPublic: false,
    tags: ["campo", "final", "vistoria"],
  },
];

// ============================================================
// Report Templates
// ============================================================
export const reportTemplates: ReportTemplate[] = [
  {
    id: "tpl-001",
    name: "Modelo Técnico Completo CTA",
    purpose: "Relatório técnico completo de inventário arbóreo com todos os indicadores, tabelas, gráficos, mapas e recomendações.",
    audience: "Responsáveis técnicos e consultorias ambientais",
    sections: ["Capa", "Sumário executivo", "Metodologia", "Indicadores gerais", "Tabelas técnicas", "Gráficos", "Mapas", "Fotos", "FCA resumidas", "Parecer técnico", "Recomendações", "Anexos", "Assinatura técnica"],
    formats: ["PDF", "XLSX", "DOCX"],
    status: "Ativo",
    icon: "ri-file-list-3-line",
  },
  {
    id: "tpl-002",
    name: "Modelo Executivo para Prefeitura",
    purpose: "Sumário executivo com indicadores-chave e recomendações para tomada de decisão pela gestão pública municipal.",
    audience: "Prefeitos, secretários e gestores públicos",
    sections: ["Capa", "Sumário executivo", "Indicadores gerais", "Gráficos", "Recomendações", "Assinatura técnica", "QR Code de validação"],
    formats: ["PDF"],
    status: "Ativo",
    icon: "ri-government-line",
  },
  {
    id: "tpl-003",
    name: "Modelo Fotográfico",
    purpose: "Relatório fotográfico com imagens georreferenciadas, legendas técnicas e anotações por indivíduo arbóreo.",
    audience: "Equipes de campo e fiscalização ambiental",
    sections: ["Capa", "Metodologia", "Fotos", "Coordenadas", "Anotações técnicas"],
    formats: ["PDF"],
    status: "Ativo",
    icon: "ri-camera-line",
  },
  {
    id: "tpl-004",
    name: "Modelo de Árvores de Risco",
    purpose: "Relatório focado em árvores com classificação de risco, com priorização e recomendações de manejo emergencial.",
    audience: "Defesa Civil e gestores de risco",
    sections: ["Capa", "Metodologia de classificação", "Lista de árvores críticas", "Mapas", "Recomendações emergenciais", "Prazos", "Assinatura técnica"],
    formats: ["PDF", "XLSX"],
    status: "Ativo",
    icon: "ri-alert-line",
  },
  {
    id: "tpl-005",
    name: "Modelo de Manejo",
    purpose: "Relatório com recomendações detalhadas de manejo arbóreo: poda, tratamento, remoção e replantio.",
    audience: "Empresas executoras e secretarias de obras",
    sections: ["Capa", "Resumo de recomendações", "Detalhamento por árvore", "Mapas", "Cronograma sugerido", "Orçamento estimado"],
    formats: ["PDF", "XLSX"],
    status: "Ativo",
    icon: "ri-scissors-line",
  },
  {
    id: "tpl-006",
    name: "Modelo de Compensação",
    purpose: "Relatório de monitoramento de compensações ambientais com indicadores de plantio, sobrevivência e vistorias.",
    audience: "Órgãos ambientais e empreendedores",
    sections: ["Capa", "Obrigação ambiental", "Indicadores de plantio", "Taxa de sobrevivência", "Vistorias", "Fotos", "CO2e preliminar", "Status"],
    formats: ["PDF", "XLSX"],
    status: "Ativo",
    icon: "ri-plant-line",
  },
  {
    id: "tpl-007",
    name: "Modelo de UC",
    purpose: "Relatório de gestão de Unidade de Conservação com inventário, plano de manejo e monitoramento.",
    audience: "Gestores de UC e órgãos ambientais",
    sections: ["Capa", "Caracterização da UC", "Inventário arbóreo", "Espécies ameaçadas", "Pressões e ameaças", "Plano de manejo", "Recomendações"],
    formats: ["PDF", "DOCX"],
    status: "Ativo",
    icon: "ri-landscape-line",
  },
  {
    id: "tpl-008",
    name: "Modelo de Carbono",
    purpose: "Relatório de estimativas de carbono urbano com metodologia, indicadores e análise por projeto e tipo de área.",
    audience: "Gestores ESG e consultorias de sustentabilidade",
    sections: ["Capa", "Aviso de estimativas preliminares", "Metodologia", "Indicadores gerais", "CO2e por projeto", "CO2e por tipo de área", "Qualidade dos dados", "Limitações"],
    formats: ["PDF", "XLSX"],
    status: "Ativo",
    icon: "ri-cloud-line",
  },
  {
    id: "tpl-009",
    name: "Modelo Documental",
    purpose: "Índice documental completo de projeto com fontes, autorizações, laudos e metadados.",
    audience: "Gestores de projeto e auditoria",
    sections: ["Capa", "Índice documental", "Resumo por tipo", "Documentos vencidos", "Autorizações", "Laudos técnicos", "Qualidade documental"],
    formats: ["PDF", "XLSX"],
    status: "Ativo",
    icon: "ri-folder-line",
  },
  {
    id: "tpl-010",
    name: "Modelo RRR",
    purpose: "Relatório de responsabilidades, restrições e autorizações conforme modelo LADM-FloraIQ.",
    audience: "Jurídico, procuradorias e órgãos de controle",
    sections: ["Capa", "Partes envolvidas", "Papéis e vínculos", "Responsabilidades ativas", "Restrições ambientais", "Autorizações", "Obrigações pendentes", "Fontes documentais"],
    formats: ["PDF", "DOCX"],
    status: "Ativo",
    icon: "ri-shield-check-line",
  },
  {
    id: "tpl-011",
    name: "Modelo de Transparência Pública",
    purpose: "Relatório de transparência para divulgação pública no portal da prefeitura com indicadores do patrimônio arbóreo.",
    audience: "Cidadãos e órgãos de controle social",
    sections: ["Capa", "Indicadores gerais", "Distribuição por bairro", "Espécies mais frequentes", "Áreas verdes", "Ações realizadas", "Planejamento futuro"],
    formats: ["PDF"],
    status: "Ativo",
    icon: "ri-eye-line",
  },
  {
    id: "tpl-012",
    name: "Modelo FCA Individual",
    purpose: "Ficha Cadastral Arbórea individual com todos os dados dendrométricos, fotos, vistorias e histórico.",
    audience: "Responsáveis técnicos e fiscalização",
    sections: ["Capa", "Dados de localização", "Dados dendrométricos", "Estado fitossanitário", "Classificação de risco", "Interferências", "Fotos", "Vistorias", "QR Code de validação"],
    formats: ["PDF"],
    status: "Ativo",
    icon: "ri-file-text-line",
  },
];

// ============================================================
// CTA Reports
// ============================================================
export const ctaReports: CtaReport[] = [
  {
    id: "cta-001",
    title: "Inventário Arbóreo Completo",
    description: "Relatório técnico completo com todos os indivíduos arbóreos cadastrados no CTA, dados dendrométricos e indicadores.",
    indicators: ["Total de árvores", "Espécies", "Distribuição espacial", "Fitossanidade", "Risco"],
    lastGenerated: "2026-06-10",
    type: "Inventário arbóreo",
  },
  {
    id: "cta-002",
    title: "Lista de Árvores Cadastradas",
    description: "Listagem completa em formato tabular com código CTA, espécie, localização e dados principais de cada indivíduo.",
    indicators: ["Código CTA", "Espécie", "Bairro", "Status de revisão"],
    lastGenerated: "2026-06-18",
    type: "Técnico",
  },
  {
    id: "cta-003",
    title: "FCA Individual",
    description: "Ficha Cadastral Arbórea individual com todos os dados, fotos, vistorias e QR Code de validação.",
    indicators: ["Dados completos", "Fotos", "Vistorias", "QR Code"],
    lastGenerated: "2026-06-15",
    type: "FCA individual",
  },
  {
    id: "cta-004",
    title: "FCA em Lote",
    description: "Geração de múltiplas FCAs em lote com opção de selecionar por projeto, bairro ou lista de códigos CTA.",
    indicators: ["Quantidade de FCAs", "Projeto", "Bairro"],
    lastGenerated: "2026-06-12",
    type: "FCA individual",
  },
  {
    id: "cta-005",
    title: "Espécies Mais Frequentes",
    description: "Ranking das espécies mais frequentes no inventário, com distribuição por família botânica e origem (nativa/exótica).",
    indicators: ["Top 10 espécies", "Famílias", "Nativas vs exóticas"],
    lastGenerated: "2026-06-08",
    type: "Espécies",
  },
  {
    id: "cta-006",
    title: "Estado Fitossanitário",
    description: "Distribuição do estado fitossanitário das árvores: bom, regular, ruim e morta, com análise por bairro e espécie.",
    indicators: ["Distribuição por estado", "Árvores em estado crítico", "Recomendações"],
    lastGenerated: "2026-06-05",
    type: "Técnico",
  },
  {
    id: "cta-007",
    title: "Classe de Risco",
    description: "Classificação de risco arbóreo com distribuição por classe e localização geográfica das árvores críticas.",
    indicators: ["Distribuição por classe", "Árvores críticas", "Mapa de risco"],
    lastGenerated: "2026-06-14",
    type: "Árvores de risco",
  },
  {
    id: "cta-008",
    title: "Manejo Recomendado",
    description: "Relatório consolidado de recomendações de manejo com quantitativos por tipo de intervenção sugerida.",
    indicators: ["Podas", "Tratamentos", "Remoções", "Replantios"],
    lastGenerated: "2026-06-09",
    type: "Manejo recomendado",
  },
  {
    id: "cta-009",
    title: "Árvores Sem Foto",
    description: "Lista de árvores cadastradas que ainda não possuem registro fotográfico vinculado.",
    indicators: ["Quantidade sem foto", "Por projeto", "Por bairro"],
    lastGenerated: "2026-06-16",
    type: "Auditoria",
  },
  {
    id: "cta-010",
    title: "Árvores Sem Espécie Identificada",
    description: "Levantamento de árvores com espécie não identificada ou pendente de confirmação botânica.",
    indicators: ["Quantidade", "Por projeto", "Prioridade de identificação"],
    lastGenerated: "2026-06-17",
    type: "Auditoria",
  },
  {
    id: "cta-011",
    title: "Árvores Pendentes de Revisão",
    description: "Lista de árvores que necessitam de revisão cadastral, com justificativa e prazo recomendado.",
    indicators: ["Pendentes", "Por projeto", "Vencidas"],
    lastGenerated: "2026-06-19",
    type: "Auditoria",
  },
];

// ============================================================
// Environmental Reports
// ============================================================
export const environmentalReports: CtaReport[] = [
  {
    id: "env-001",
    title: "Áreas Verdes Cadastradas",
    description: "Relatório completo das áreas verdes municipais com indicadores por tipo, área total e estado de conservação.",
    indicators: ["Total de áreas", "Área total (ha)", "Distribuição por tipo"],
    lastGenerated: "2026-06-10",
    type: "Área verde",
  },
  {
    id: "env-002",
    title: "Unidades de Conservação",
    description: "Relatório de gestão das UCs municipais com inventário, plano de manejo e monitoramento ambiental.",
    indicators: ["Total de UCs", "Área protegida (ha)", "Status do plano de manejo"],
    lastGenerated: "2026-06-05",
    type: "Unidade de Conservação",
  },
  {
    id: "env-003",
    title: "APPs Urbanas",
    description: "Diagnóstico das Áreas de Preservação Permanente urbanas com estado de conservação e pressões antrópicas.",
    indicators: ["Total de APPs", "Extensão (km)", "Estado de conservação"],
    lastGenerated: "2026-06-07",
    type: "Área verde",
  },
  {
    id: "env-004",
    title: "Áreas de Recuperação",
    description: "Monitoramento de áreas em processo de recuperação ambiental com indicadores de evolução.",
    indicators: ["Áreas em recuperação", "Área total (ha)", "Estágio da recuperação"],
    lastGenerated: "2026-06-12",
    type: "Compensação ambiental",
  },
  {
    id: "env-005",
    title: "Árvores em Áreas Ambientais",
    description: "Inventário arbóreo das árvores localizadas dentro de áreas verdes, UCs e APPs.",
    indicators: ["Total de árvores", "Por tipo de área", "Fitossanidade"],
    lastGenerated: "2026-06-14",
    type: "Inventário arbóreo",
  },
  {
    id: "env-006",
    title: "Risco Crítico em Áreas Verdes",
    description: "Identificação de árvores com risco crítico dentro de áreas verdes públicas, com mapas e recomendações.",
    indicators: ["Árvores críticas", "Por área verde", "Prioridade de intervenção"],
    lastGenerated: "2026-06-08",
    type: "Árvores de risco",
  },
  {
    id: "env-007",
    title: "Documentos Vinculados a Áreas Ambientais",
    description: "Levantamento de documentos, autorizações e laudos vinculados a áreas verdes, UCs e APPs.",
    indicators: ["Total de documentos", "Por tipo de área", "Documentos vencidos"],
    lastGenerated: "2026-06-09",
    type: "Documental",
  },
  {
    id: "env-008",
    title: "Relatório Ambiental Executivo",
    description: "Sumário executivo das áreas ambientais municipais para gestores, com indicadores e recomendações.",
    indicators: ["Indicadores consolidados", "Áreas prioritárias", "Recomendações"],
    lastGenerated: "2026-06-15",
    type: "Executivo",
  },
];

// ============================================================
// Carbon Reports
// ============================================================
export const carbonReports: CtaReport[] = [
  {
    id: "car-001",
    title: "Relatório Preliminar de Carbono Urbano",
    description: "Estimativas preliminares de biomassa, carbono estocado e CO2e do patrimônio arbóreo urbano.",
    indicators: ["CO2e total", "Carbono (t C)", "Biomassa (t)", "Árvores consideradas"],
    lastGenerated: "2026-06-18",
    type: "Carbono",
  },
  {
    id: "car-002",
    title: "Biomassa Estimada por Projeto",
    description: "Distribuição da biomassa estimada por projeto de inventário, com metodologia e nível de confiança.",
    indicators: ["Biomassa por projeto", "Metodologia", "Confiança"],
    lastGenerated: "2026-06-17",
    type: "Carbono",
  },
  {
    id: "car-003",
    title: "CO2e por Projeto",
    description: "Estimativas de CO2 equivalente por projeto, com análise comparativa e evolução temporal.",
    indicators: ["CO2e por projeto", "Comparativo", "Evolução"],
    lastGenerated: "2026-06-18",
    type: "Carbono",
  },
  {
    id: "car-004",
    title: "CO2e por Área Verde e UC",
    description: "Distribuição das estimativas de CO2e entre áreas verdes e Unidades de Conservação.",
    indicators: ["CO2e por área verde", "CO2e por UC", "Ranking"],
    lastGenerated: "2026-06-18",
    type: "Carbono",
  },
  {
    id: "car-005",
    title: "Compensações Ambientais",
    description: "Monitoramento de compensações ambientais com indicadores de carbono e sobrevivência de mudas.",
    indicators: ["Compensações ativas", "Mudas plantadas", "CO2e preliminar"],
    lastGenerated: "2026-06-16",
    type: "Compensação ambiental",
  },
  {
    id: "car-006",
    title: "Qualidade dos Dados de Carbono",
    description: "Painel de qualidade dos dados utilizados nas estimativas de carbono, com métricas de completude.",
    indicators: ["% DAP preenchido", "% altura preenchida", "% espécie identificada", "Confiança"],
    lastGenerated: "2026-06-17",
    type: "Auditoria",
  },
  {
    id: "car-007",
    title: "Relatório ESG Ambiental",
    description: "Relatório corporativo de indicadores ambientais para ESG, com dados de carbono e sustentabilidade.",
    indicators: ["Indicadores ESG", "Carbono", "Compensações", "Sustentabilidade"],
    lastGenerated: "2026-06-19",
    type: "Carbono",
  },
];

// ============================================================
// Documentary / RRR Reports
// ============================================================
export const documentaryReports: CtaReport[] = [
  {
    id: "doc-001",
    title: "Índice Documental do Projeto",
    description: "Índice completo de documentos vinculados ao projeto, com classificação por tipo e status.",
    indicators: ["Total de documentos", "Por tipo", "Status"],
    lastGenerated: "2026-06-10",
    type: "Documental",
  },
  {
    id: "doc-002",
    title: "Documentos Vencidos",
    description: "Levantamento de documentos vencidos ou próximos do vencimento, com alertas e ações recomendadas.",
    indicators: ["Vencidos", "A vencer 30 dias", "A vencer 60 dias"],
    lastGenerated: "2026-06-15",
    type: "Documental",
  },
  {
    id: "doc-003",
    title: "Autorizações Ambientais",
    description: "Compilado de autorizações ambientais emitidas com status de vigência e execução.",
    indicators: ["Autorizações ativas", "Vencidas", "Pendentes de execução"],
    lastGenerated: "2026-06-12",
    type: "Documental",
  },
  {
    id: "doc-004",
    title: "Laudos Técnicos",
    description: "Relatório de laudos fitossanitários, pareceres de risco e laudos técnicos emitidos no período.",
    indicators: ["Total de laudos", "Por tipo", "Responsáveis técnicos"],
    lastGenerated: "2026-06-08",
    type: "Documental",
  },
  {
    id: "doc-005",
    title: "Fontes por Árvore CTA",
    description: "Mapeamento de fontes documentais vinculadas a cada indivíduo arbóreo do CTA.",
    indicators: ["Árvores com fonte", "Sem documento", "Tipos de fonte"],
    lastGenerated: "2026-06-14",
    type: "Documental",
  },
  {
    id: "doc-006",
    title: "Responsabilidades Ativas",
    description: "Relatório de responsabilidades ambientais ativas conforme modelo LADM-FloraIQ.",
    indicators: ["Partes responsáveis", "Obrigações ativas", "Status"],
    lastGenerated: "2026-06-09",
    type: "RRR / Governança",
  },
  {
    id: "doc-007",
    title: "Restrições Ambientais",
    description: "Mapeamento de restrições ambientais vigentes vinculadas a unidades ambientais.",
    indicators: ["Restrições ativas", "Por tipo", "Unidades afetadas"],
    lastGenerated: "2026-06-11",
    type: "RRR / Governança",
  },
  {
    id: "doc-008",
    title: "Obrigações Pendentes",
    description: "Levantamento de obrigações ambientais pendentes de cumprimento com prazos e responsáveis.",
    indicators: ["Obrigações pendentes", "Vencidas", "Em andamento"],
    lastGenerated: "2026-06-13",
    type: "RRR / Governança",
  },
  {
    id: "doc-009",
    title: "Compensações Monitoradas",
    description: "Monitoramento de compensações ambientais com indicadores de plantio e vistorias.",
    indicators: ["Compensações ativas", "Mudas", "Sobrevivência"],
    lastGenerated: "2026-06-16",
    type: "Compensação ambiental",
  },
  {
    id: "doc-010",
    title: "Governança LADM-FloraIQ",
    description: "Relatório integrado de governança conforme modelo LADM, vinculando partes, fontes, unidades e RRR.",
    indicators: ["Partes", "Fontes", "Unidades", "RRR"],
    lastGenerated: "2026-06-18",
    type: "RRR / Governança",
  },
];

// ============================================================
// GIS Exports
// ============================================================
export const gisExports: GisExport[] = [
  {
    id: "gis-001",
    layer: "Árvores CTA — Pontos",
    project: "CTA Urbano Campinas",
    municipality: "Campinas",
    attributeFilter: "Todas as árvores",
    srid: "EPSG:4326",
    format: "GeoJSON",
    featureCount: 10932,
    lastExported: "2026-06-18",
    status: "Disponível",
  },
  {
    id: "gis-002",
    layer: "Áreas Verdes — Polígonos",
    project: "Áreas Verdes Municipais",
    municipality: "Campinas",
    attributeFilter: "Todas as áreas",
    srid: "EPSG:4326",
    format: "GeoJSON",
    featureCount: 64,
    lastExported: "2026-06-15",
    status: "Disponível",
  },
  {
    id: "gis-003",
    layer: "UCs — Polígonos",
    project: "Gestão UC Mata Verde",
    municipality: "Campinas",
    attributeFilter: "Todas as UCs",
    srid: "EPSG:4326",
    format: "GeoJSON",
    featureCount: 9,
    lastExported: "2026-06-12",
    status: "Disponível",
  },
  {
    id: "gis-004",
    layer: "Árvores de Risco Crítico — Pontos",
    project: "CTA Urbano Campinas",
    municipality: "Campinas",
    attributeFilter: "Risco = Crítico",
    srid: "EPSG:4326",
    format: "GeoJSON",
    featureCount: 142,
    lastExported: "2026-06-14",
    status: "Disponível",
  },
  {
    id: "gis-005",
    layer: "Manejo Recomendado — Pontos",
    project: "CTA Urbano Campinas",
    municipality: "Campinas",
    attributeFilter: "Com recomendação de manejo",
    srid: "EPSG:4326",
    format: "Shapefile",
    featureCount: 847,
    lastExported: "2026-06-10",
    status: "Disponível",
  },
  {
    id: "gis-006",
    layer: "Espécies por Bairro — Heatmap",
    project: "CTA Urbano Campinas",
    municipality: "Campinas",
    attributeFilter: "Agrupado por espécie e bairro",
    srid: "EPSG:4326",
    format: "GeoJSON",
    featureCount: 42,
    lastExported: "2026-06-08",
    status: "Disponível",
  },
  {
    id: "gis-007",
    layer: "Vistorias Georreferenciadas",
    project: "CTA Urbano Campinas",
    municipality: "Campinas",
    attributeFilter: "Vistorias 2026",
    srid: "EPSG:4326",
    format: "GeoJSON",
    featureCount: 1847,
    lastExported: "2026-06-16",
    status: "Em processamento",
  },
  {
    id: "gis-008",
    layer: "Camada Pública — Portal da Prefeitura",
    project: "Transparência Municipal",
    municipality: "Campinas",
    attributeFilter: "Dados públicos",
    srid: "EPSG:4326",
    format: "KML/KMZ",
    featureCount: 10932,
    lastExported: "2026-06-05",
    status: "Disponível",
  },
];

// ============================================================
// Recent Reports (for sidebar panel)
// ============================================================
export const recentReports = reports
  .filter((r) => r.status !== "Rascunho" && r.generatedAt)
  .sort((a, b) => b.generatedAt.localeCompare(a.generatedAt))
  .slice(0, 8);

// ============================================================
// Pending Reports
// ============================================================
export const pendingReports = [
  {
    id: "pend-001",
    title: "Relatório Final — CTA Vinhedo",
    project: "CTA Vinhedo",
    reason: "Projeto sem relatório final",
    priority: "Alta",
    dueDate: "2026-07-15",
    action: "Gerar relatório final",
  },
  {
    id: "pend-002",
    title: "Relatório de Carbono — Guarulhos",
    project: "Inventário Guarulhos",
    reason: "Relatório em revisão aguardando aprovação",
    priority: "Média",
    dueDate: "2026-07-05",
    action: "Revisar e aprovar",
  },
  {
    id: "pend-003",
    title: "Relatório de Documentos Vencidos",
    project: "Gestão Documental Municipal",
    reason: "Relatório com erro de geração",
    priority: "Alta",
    dueDate: "2026-07-02",
    action: "Corrigir e gerar novamente",
  },
  {
    id: "pend-004",
    title: "Relatório de Auditoria — Zona Sul",
    project: "CTA Zona Sul",
    reason: "Relatório desatualizado (mais de 90 dias)",
    priority: "Baixa",
    dueDate: "2026-07-20",
    action: "Atualizar dados e gerar",
  },
  {
    id: "pend-005",
    title: "Exportação GIS — Shapefile UCs",
    project: "Gestão UC Mata Verde",
    reason: "Exportação GIS pendente",
    priority: "Média",
    dueDate: "2026-07-08",
    action: "Exportar camada",
  },
  {
    id: "pend-006",
    title: "Relatório RRR — Loteamento Green Park",
    project: "Loteamento Green Park",
    reason: "Relatório aguardando aprovação do responsável técnico",
    priority: "Média",
    dueDate: "2026-07-10",
    action: "Solicitar aprovação",
  },
  {
    id: "pend-007",
    title: "Relatório de Transparência — 2º Trimestre",
    project: "CTA Urbano Campinas",
    reason: "Relatório trimestral pendente de publicação",
    priority: "Alta",
    dueDate: "2026-07-01",
    action: "Gerar e publicar",
  },
  {
    id: "pend-008",
    title: "Relatório Fotográfico — Quadra 22",
    project: "CTA Urbano Campinas",
    reason: "Relatório em processamento há mais de 24h",
    priority: "Baixa",
    dueDate: "2026-07-03",
    action: "Verificar status de geração",
  },
];

// ============================================================
// Summary stats
// ============================================================
export const reportsSummary = {
  totalGenerated: 847,
  technicalReports: 326,
  photoReports: 184,
  gisReports: 96,
  inProgress: 12,
  pendingReview: 28,
  withError: 4,
  availableTemplates: 24,
};

export const generatorReportTypes = [
  { id: "tecnico-inventario", name: "Relatório Técnico de Inventário Arbóreo", icon: "ri-file-list-3-line", description: "Relatório completo com indicadores, tabelas e recomendações" },
  { id: "executivo", name: "Relatório Executivo Municipal", icon: "ri-government-line", description: "Sumário executivo para tomada de decisão" },
  { id: "fotografico", name: "Relatório Fotográfico", icon: "ri-camera-line", description: "Fotos georreferenciadas com legendas técnicas" },
  { id: "risco", name: "Relatório de Árvores de Risco", icon: "ri-alert-line", description: "Classificação e priorização de risco" },
  { id: "manejo", name: "Relatório de Manejo Recomendado", icon: "ri-scissors-line", description: "Recomendações de poda, tratamento e remoção" },
  { id: "especies", name: "Relatório por Espécie", icon: "ri-leaf-line", description: "Distribuição e análise de espécies" },
  { id: "bairro", name: "Relatório por Bairro", icon: "ri-map-pin-line", description: "Inventário por bairro ou região" },
  { id: "area-verde", name: "Relatório de Área Verde", icon: "ri-landscape-line", description: "Indicadores e estado de conservação" },
  { id: "uc", name: "Relatório de UC", icon: "ri-shield-star-line", description: "Gestão de Unidade de Conservação" },
  { id: "carbono", name: "Relatório de Carbono", icon: "ri-cloud-line", description: "Estimativas de CO2e e biomassa" },
  { id: "compensacao", name: "Relatório de Compensação Ambiental", icon: "ri-plant-line", description: "Monitoramento de compensações" },
  { id: "documental", name: "Relatório Documental", icon: "ri-folder-line", description: "Índice e fontes documentais" },
  { id: "rrr", name: "Relatório RRR", icon: "ri-shield-check-line", description: "Responsabilidades, restrições e autorizações" },
  { id: "fca", name: "FCA Individual", icon: "ri-file-text-line", description: "Ficha Cadastral Arbórea individual" },
  { id: "gis", name: "Exportação GIS", icon: "ri-map-2-line", description: "Exportação geoespacial de camadas" },
];

export const contentSections = [
  { id: "capa", label: "Capa" },
  { id: "sumario", label: "Sumário executivo" },
  { id: "metodologia", label: "Metodologia" },
  { id: "indicadores", label: "Indicadores gerais" },
  { id: "tabelas", label: "Tabelas técnicas" },
  { id: "graficos", label: "Gráficos" },
  { id: "mapas", label: "Mapas" },
  { id: "fotos", label: "Fotos" },
  { id: "fca", label: "FCA resumidas" },
  { id: "parecer", label: "Parecer técnico" },
  { id: "recomendacoes", label: "Recomendações" },
  { id: "anexos", label: "Anexos" },
  { id: "assinatura", label: "Assinatura técnica" },
  { id: "qrcode", label: "QR Code de validação" },
  { id: "fontes", label: "Fontes documentais" },
];

export const outputFormats = ["PDF", "XLSX", "CSV", "DOCX", "GeoJSON", "Shapefile", "KML/KMZ"];